import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import Button from '../Button';
import Icon from '../Icon';
import LoadingIndicator from '../LoadingIndicator';
import { getQTipData } from '../Tooltip/qTip.utilities';
import type { IconType } from '../Icon/Icon.types';
import type { ButtonWithDropdownProps, DropdownItems } from './ButtonWithDropdown.types';

const borderClassNames = `
  tw:rounded-[0.5rem]
  tw:border
  tw:border-solid
  tw:border-sq-disabled-gray
  tw:dark:border-gray-500
`;

const bgClassNames = `tw:bg-sq-white tw:dark:bg-sq-dark-background`;

const renderDropdownItemIcon = ({
  item,
  iconExtraClassNames,
  spinnerExtraClassNames = iconExtraClassNames,
  iconType = 'text',
}: {
  item: DropdownItems;
  iconExtraClassNames: string;
  spinnerExtraClassNames?: string;
  iconType?: IconType;
}) => {
  if (item.inProgress) {
    return (
      <LoadingIndicator
        size={16}
        color="currentColor"
        testId={item.iconTestId}
        customId={item.iconCustomId}
        extraClassNames={spinnerExtraClassNames}
      />
    );
  }

  if (!item.icon) {
    return null;
  }

  return (
    <Icon
      icon={item.icon}
      testId={item.iconTestId}
      type={iconType}
      color={item.iconColor}
      customId={item.iconCustomId}
      extraClassNames={iconExtraClassNames}
    />
  );
};

const ButtonWithDropdown = (props: ButtonWithDropdownProps) => {
  const {
    label,
    variant = 'outline',
    size = 'sm',
    icon,
    iconStyle = 'text',
    iconColor,
    iconPosition = 'left',
    iconPrefix,
    extraClassNames = '',
    testId,
    id,
    disabled = false,
    inProgress = false,
    ...tooltipProps
  } = props;

  return (
    <Dropdown {...props}>
      <DropdownMenu.Trigger asChild id={id} disabled={disabled}>
        <Button
          label={label}
          variant={variant}
          size={size}
          icon={icon}
          iconStyle={iconStyle}
          iconColor={iconColor}
          iconPosition={iconPosition}
          iconPrefix={iconPrefix}
          extraClassNames={extraClassNames}
          testId={testId}
          disabled={disabled}
          inProgress={inProgress}
          tooltip={tooltipProps.tooltip}
          tooltipOptions={{
            delay: tooltipProps.tooltipDelay,
            position: tooltipProps.tooltipPlacement,
          }}
        />
      </DropdownMenu.Trigger>
    </Dropdown>
  );
};

export const Dropdown = ({
  dropdownItems,
  id,
  children,
  contentExtraClassNames = '',
  containerTestId,
  align = 'end',
  placement = 'bottom',
  placementOffset = 5,
  alignOffset = -35,
  hasArrow = false,
  onOpenChange,
  isOpen,
  setFocusOnTriggerOnClose = true,
  keepFocusInsideDropdown = true,
  onContainerClick,
}: ButtonWithDropdownProps) => {
  return (
    <DropdownMenu.Root defaultOpen={false} open={isOpen} onOpenChange={onOpenChange} modal={keepFocusInsideDropdown}>
      {children}
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          onClick={onContainerClick}
          sideOffset={placementOffset}
          side={placement}
          align={align}
          alignOffset={alignOffset}
          asChild
          onCloseAutoFocus={(e) => !setFocusOnTriggerOnClose && e.preventDefault()}
          className="tw:outline-none tw:focus-visible:outline-none">
          <div
            data-testid={containerTestId}
            className={clsx(
              bgClassNames,
              `tw:relative tw:z-1200 tw:min-w-6 tw:py-2 tw:outline-none tw:focus-visible:outline-none
              tw:data-[side=bottom]:animate-slideUpAndFade
              tw:data-[side=left]:animate-slideRightAndFade
              tw:data-[side=right]:animate-slideLeftAndFade
              tw:data-[side=top]:animate-slideDownAndFade`,
              borderClassNames,
              contentExtraClassNames,
            )}>
            {hasArrow && (
              <DropdownMenu.Arrow asChild>
                <div className="tw:-mt-sq-7 tw:h-sq-15 tw:w-sq-15 tw:rotate-45 tw:border-r tw:border-b tw:border-sq-disabled-gray tw:bg-sq-white tw:fill-transparent tw:dark:border-gray-500 tw:dark:bg-sq-dark-background" />
              </DropdownMenu.Arrow>
            )}
            {dropdownItems.map((item, index) => {
              const tooltipData = getQTipData(item);

              if (item.isLabel) {
                return (
                  <DropdownMenu.Label
                    key={(item.id || '') + index}
                    className={item.itemExtraClassNames}
                    {...tooltipData}>
                    {(item.icon || item.inProgress) &&
                      renderDropdownItemIcon({
                        item,
                        iconExtraClassNames: 'tw:text-sq-text-color tw:dark:text-sq-white tw:w-4.5',
                        spinnerExtraClassNames: 'tw:text-sq-text-color tw:dark:text-sq-white',
                      })}
                    <div
                      data-testid={item.labelTestId}
                      className={`tw:ml-1 tw:text-sq-13 ${
                        item.disabled ? 'tw:text-sq-disabled-gray' : 'tw:text-sq-text-color tw:dark:text-sq-white'
                      } tw:font-normal tw:not-italic ${item.labelClasses}`}>
                      {item.label}
                    </div>
                  </DropdownMenu.Label>
                );
              }
              if (Array.isArray(item.subMenuItems) && item.subMenuItems.length > 0) {
                return (
                  <DropdownMenu.Sub key={(item.id || item.icon || '') + index}>
                    <DropdownMenu.SubTrigger
                      id={id}
                      className={`tw:relative tw:flex tw:h-sq-27 tw:cursor-pointer tw:items-center tw:justify-between tw:pl-sq-19 tw:leading-none tw:outline-none tw:select-none tw:hover:bg-sq-gray-highlight tw:data-disabled:pointer-events-none tw:dark:text-sq-white tw:dark:hover:bg-sq-gray-highlight-dark ${
                        item.itemExtraClassNames || ''
                      }`}
                      data-testid={item.testId}
                      disabled={item.disabled}
                      {...tooltipData}>
                      <div className="tw:flex">
                        {(item.icon || item.inProgress) && (
                          <div className="tw:min-w-5">
                            {renderDropdownItemIcon({
                              item,
                              iconExtraClassNames: `tw:w-4.5 ${
                                item.disabled
                                  ? 'tw:text-sq-disabled-gray!'
                                  : 'tw:text-sq-text-color tw:dark:text-sq-white'
                              } ${item.iconExtraClassNames || ''}`,
                              spinnerExtraClassNames: `${
                                item.disabled
                                  ? 'tw:text-sq-disabled-gray!'
                                  : 'tw:text-sq-text-color tw:dark:text-sq-white'
                              } ${item.iconExtraClassNames || ''}`,
                            })}
                          </div>
                        )}
                        <div
                          data-testid={item.labelTestId}
                          data-customid={item.labelCustomId}
                          className={`tw:ml-1 tw:text-sq-13 ${
                            item.disabled && 'tw:opacity-30!'
                          } tw:font-semibold tw:not-italic ${item.labelClasses}`}>
                          {item.label}
                        </div>
                      </div>
                      <div className="tw:flex tw:items-center tw:justify-center">
                        <Icon
                          size="sm"
                          icon="fc-arrow-dropdown tw:rotate-270"
                          extraClassNames={`${
                            item.disabled ? 'tw:text-sq-disabled-gray' : 'tw:text-sq-text-color tw:dark:text-sq-white'
                          } tw:ml-4 tw:mr-2 tw:text-2`}
                        />
                      </div>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.SubContent className="tw:outline-none tw:focus-visible:outline-none">
                        <div
                          data-testid={containerTestId}
                          className={clsx(
                            bgClassNames,
                            'tw:relative tw:z-1000 tw:min-w-6 tw:py-2 tw:outline-none tw:focus-visible:outline-none',
                            'tw:data-[side=bottom]:animate-slideUpAndFade',
                            'tw:data-[side=left]:animate-slideRightAndFade',
                            'tw:data-[side=right]:animate-slideLeftAndFade',
                            'tw:data-[side=top]:animate-slideDownAndFade',
                            borderClassNames,
                          )}>
                          {item.subMenuItems.map((subItem, subIndex) => {
                            return (
                              <DropdownMenu.Item
                                key={subItem.label + subIndex}
                                onSelect={subItem.onClick}
                                data-qtip-text={subItem.tooltip}
                                className="tw:relative tw:flex tw:h-sq-27 tw:cursor-pointer tw:items-center tw:px-sq-19 tw:leading-none tw:outline-none tw:select-none tw:hover:bg-sq-gray-highlight tw:data-disabled:pointer-events-none tw:dark:text-sq-white tw:dark:hover:bg-sq-gray-highlight-dark"
                                disabled={subItem.disabled}>
                                {subItem.iconClass && (
                                  <Icon
                                    icon={subItem.iconClass}
                                    type="text"
                                    extraClassNames={`tw:w-4.5 ${item.disabled && 'tw:opacity-30!'}`}
                                  />
                                )}
                                <div
                                  className={`tw:ml-1 tw:text-sq-13 ${
                                    subItem.disabled
                                      ? 'tw:text-sq-disabled-gray'
                                      : 'tw:text-sq-text-color tw:dark:text-sq-white'
                                  } tw:font-normal tw:not-italic ${item.labelClasses}`}>
                                  {subItem.label}
                                </div>
                              </DropdownMenu.Item>
                            );
                          })}
                        </div>
                      </DropdownMenu.SubContent>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Sub>
                );
              }
              return (
                <div key={(item.id || '') + index} {...tooltipData}>
                  <DropdownMenu.Item
                    key={(item.id || item.icon || '') + index}
                    data-customid={item.itemCustomId}
                    onSelect={(e) => {
                      item.onClick(e);
                    }}
                    className={`tw:relative tw:flex tw:min-h-sq-27 tw:cursor-pointer tw:items-center tw:px-sq-19 tw:leading-none tw:outline-none tw:select-none tw:hover:bg-sq-gray-highlight tw:data-disabled:pointer-events-none tw:dark:text-sq-white tw:dark:hover:bg-sq-gray-highlight-dark ${
                      item.itemExtraClassNames || ''
                    }`}
                    data-testid={item.testId}
                    disabled={item.disabled}>
                    {(item.icon || item.inProgress) && (
                      <div className="tw:min-w-5">
                        {renderDropdownItemIcon({
                          item,
                          iconType: (item.iconType || 'text') as IconType,
                          iconExtraClassNames: `tw:w-sq-4.5 ${item.disabled && 'tw:opacity-30!'} ${
                            item.iconExtraClassNames || ''
                          }`,
                          spinnerExtraClassNames: `${item.disabled && 'tw:opacity-30!'} ${
                            item.iconExtraClassNames || ''
                          }`,
                        })}
                      </div>
                    )}
                    <div
                      data-testid={item.labelTestId}
                      data-customid={item.labelCustomId}
                      className={`tw:ml-1 tw:text-sq-13 ${
                        item.disabled ? 'tw:text-sq-disabled-gray' : 'tw:text-sq-text-color tw:dark:text-sq-white'
                      } tw:font-normal tw:not-italic ${item.labelClasses || ''}`}>
                      {item.label}
                    </div>
                  </DropdownMenu.Item>
                  {item.hasDivider && (
                    <DropdownMenu.Separator
                      data-testid={`dropdown-divider-${index}`}
                      className="tw:my-2 tw:h-px tw:bg-sq-disabled-gray tw:dark:bg-gray-500"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default ButtonWithDropdown;
